import React, { useState, useCallback, ReactElement, useEffect, Fragment } from 'react';
import { IconButton, IView, Link } from '@framework-controls/core';
import parse from 'html-react-parser';
import { strings } from 'src/localization';
import css from './AdditionalService.module.scss';
import { IconHelpCircle, IconHelpCircleAlt } from 'src/content/icons';
import { WeOweRec, Recom, Recall, SpecData, VehicleRec, ApptCust, QuickTimeInp, QuickTimes, Appointment, CheckedRecall, MaintenanceContract, ascCtaInteractionInput, ascElementConfigurationInput } from 'src/models';
import AdditionalServicePanel from '../../../components/AdditionalServices/AdditionalService/AdditionalServicePanel/containers/AdditionalServicePanel';
import MaintenanceContractCard from 'src/components/MaintenanceContractCard/MaintenanceContractCard';
import { ascCtaInteraction, getElementColor, ascElementConfiguration } from 'src/helpers/EventHelper/EventHelper';
import { ComponentMapIds } from 'src/components/map';
import classNames from 'classnames';
import { THEME } from 'src/constants/ThemeNames';

export interface IProps {
    weOwes: WeOweRec[];
    recom: Recom[];
    recalls: Recall[];
    maintContracts: MaintenanceContract[];
    type: string;
    inquiryInvoked: boolean;
    inquiryReturned: boolean;
    inquiryError: boolean;
    MCReturned: boolean;
    MCError: boolean;
    specData?: SpecData;
    vehicles: VehicleRec[];
    selectedVehicle?: string;
    getRecalls: () => void;
    setWaitOnRecall: (value: boolean) => void;
    setNoAutoRecall: (value: boolean) => void;
    getMaintContracts: () => void;
    setWaitOnMC: (value: boolean) => void;
    vehLayoutCard?: boolean;
    inqryRtnState?: (inp: { invoked: boolean, returned: boolean }) => void;
    setRecallLength?: (inp: number) => void;
    setMCLength?: (inpt: number) => void;
    setRecommStatus?: (inp: number) => void;
    apptCust: ApptCust;
    theme: string;
    getQuickTimes: (inp: QuickTimeInp) => void;
    quickTimes: QuickTimes;
    appointment: Appointment;
    profile: string;
    checkedRecalls: CheckedRecall[];
    mainPaneViewOrder: IView[];
    waitOnMC: boolean;
    waitOnRecall: boolean;
    noAutoRecall: boolean;
    toyotaSSC2Launched: string;
    lexusSSC2Launched: string;
}

const AdditionalService = (props: IProps) => {
    let serviceTitle = '';
    let serviceDescription: ReactElement | string | Document | ChildNode | null = '';
    let serviceType = '';
    let serviceList: ReactElement[] | ReactElement | null = null;
    const [displayHelpMessage, setDisplayHelpMessage] = useState(false);
    const toyLexSSC2Launched =
        props.toyotaSSC2Launched === 'Y' ||
        props.lexusSSC2Launched === 'Y';
    // used to determine whether the user is on or has passed the QuickTimes or CalendarSelect screen
    // const quickTimesBrowsed = props.mainPaneViewOrder.some(pane => (
    //     pane.viewId === 'QuickTime' || pane.viewId === 'CalendarSelect') ? true : false);

    // useEffect(() => {
    //     if (props.type === 'Recall' && !quickTimesBrowsed && props.apptCust.custLoggedIn) {
    //         const quickSelectedVeh = props.selectedVehicle ? props.selectedVehicle : 0;
    //         const vehicle: VehicleRec = props.vehicles[quickSelectedVeh];
    //         const quickTimesInp = {
    //             specProfile: props.profile,
    //             department: props.appointment.department,
    //             lastAdv: vehicle.lastAdv,
    //             lastTrans: vehicle.lastTrans,
    //             recallSelected: props.checkedRecalls.length > 0,
    //         };
    //         props.setRecallSelected!(props.checkedRecalls.length > 0);
    //         props.getQuickTimes(quickTimesInp);
    //     }
    // }, [props.checkedRecalls,
    //     props.profile,
    //     props.appointment.department,
    //     props.getQuickTimes,
    //     props.type,
    //     quickTimesBrowsed,
    //     props.apptCust.custLoggedIn,
    //     props.selectedVehicle,
    //     props.vehicles,
    // ]);

    const onClick = useCallback((e: any) => {
        setDisplayHelpMessage(!displayHelpMessage);
        const elementColor = getElementColor(e, 'color');
        const ascCtaInteractionInp : ascCtaInteractionInput = {
            pageType: 'service',
            elementText: strings.RecallsHeader,
            elementColor: elementColor,
            elementOrder: '',
            elementType: 'scheduling_tool',
            elementSubType: 'information_link',
            eventAction: 'click',
            eventActionResult: 'next_step',
            linkUrl: '',
        };
        ascCtaInteraction(ascCtaInteractionInp, e, props.vehicles, props.selectedVehicle);
        const ascElementConfigurationInp: ascElementConfigurationInput = {
            pageType: 'service',
            elementText: strings.RecallsHeader,
            elementType: 'scheduling_tool',
            elementColor: elementColor,
            eventAction: 'click',
            eventActionResult: 'next_step',
            linkUrl: '',
        };
        ascElementConfiguration(ascElementConfigurationInp, e);
    }, [displayHelpMessage, props.vehicles, props.selectedVehicle]);

    const recallLink = useCallback((e: any) => {
        const ascCtaInteractionInp : ascCtaInteractionInput = {
            pageType: 'service',
            elementText: strings.recallLink,
            elementColor: '',
            elementOrder: '',
            elementType: 'popup',
            elementSubType: 'information_link',
            eventAction: 'click',
            eventActionResult: 'open',
            linkUrl: '',
        };
        ascCtaInteraction(ascCtaInteractionInp, e, props.vehicles, props.selectedVehicle);
        const ascElementConfigurationInp: ascElementConfigurationInput = {
            pageType: 'service',
            elementText: strings.recallLink,
            elementType: 'popup',
            elementColor: '',
            eventAction: 'click',
            eventActionResult: 'open',
            linkUrl: '',
        };
        ascElementConfiguration(ascElementConfigurationInp, e);
    }, [props.vehicles, props.selectedVehicle]);

    // Audi only: plain left click opens the sized recall popup; modified and middle clicks use the browser default.
    const recallLinkHandler = useCallback((e: React.MouseEvent<HTMLAnchorElement>) => {
        recallLink(e);
        if (e.defaultPrevented || e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) {
            return;
        }
        e.preventDefault();
        window.open(strings.recallLink, '_blank', 'width=910,height=660,menubar=no,toolbar=no');
    }, [recallLink]);

    const recallHelpLinkClass = classNames(
        css.recallHelpLink,
        {
            [css.recallHelpLinkBMW]: props.theme === 'bmw',
            [css.colorIsLinkText]: props.theme === 'infiniti' || props.theme === 'nissan' || props.theme === 'mercedesBenz' ||
                props.theme === 'acura',
            [css.recallHelpLinkSubaru]: props.theme === 'subaru',
        },
    );

    let recallQuestionClassName = css.question;
    if (props.theme === 'volkswagen') {
        recallQuestionClassName = css.questionVW;
    }
    if (props.theme === 'bmw') {
        recallQuestionClassName = css.questionBMW;
    }
    if (props.theme === THEME.AUDI) {
        recallQuestionClassName = css.questionAudi;
    }

    let helpDisplay = css.helpDisplay;
    let helpDisplayMini = css.helpDisplayNone;
    if (props.theme === 'mini') {
        helpDisplay = css.helpDisplayNone;
        helpDisplayMini = css.helpDisplay;
    }

    useEffect(() => {
        if (props.type === 'Recall' && !props.inquiryReturned && !props.inquiryError && !props.waitOnRecall) {
            props.getRecalls();
            props.setWaitOnRecall(true);
        }
        if (props.inqryRtnState && !props.inquiryReturned && props.inquiryInvoked && !props.inquiryError) {
            props.inqryRtnState({ invoked: true, returned: false });
        } else {
            if (props.inqryRtnState) {
                props.inqryRtnState({ invoked: true, returned: true });
                if (props.setRecallLength) {
                    props.setRecallLength(props.recalls?.length);
                }
            }
        }
        if (props.setRecommStatus) {
            props.setRecommStatus(props.recom?.length);
        }
        const currentPane = props.mainPaneViewOrder[props.mainPaneViewOrder.length - 1];

        if (props.type === 'Maintenance Contract' && !props.MCReturned && !props.MCError && !props.waitOnMC &&
            (currentPane?.viewId === ComponentMapIds.VehLayout)) {
            props.getMaintContracts();
            props.setWaitOnMC(true);
        }
        if (props.inqryRtnState && !props.MCReturned && props.inquiryInvoked && !props.MCError) {
            props.inqryRtnState({ invoked: true, returned: false });
        } else {
            if (props.inqryRtnState) {
                props.inqryRtnState({ invoked: true, returned: true });
                if (props.setMCLength) {
                    props.setMCLength(props.maintContracts?.length);
                }
            }
        }
    }, [
        props.type,
        props.getRecalls,
        props.inquiryReturned,
        props.inquiryError,
        props.inqryRtnState,
        props.inquiryInvoked,
        props.recalls,
        props.setRecallLength,
        props.recom,
        props.setRecommStatus,
        props.setWaitOnRecall,
        props.MCError,
        props.MCReturned,
        props.getMaintContracts,
        props.setMCLength,
        props.maintContracts,
        props.setWaitOnMC,
        props.vehicles,
        props.selectedVehicle,
        props.waitOnMC,
        props.waitOnRecall,
        props.mainPaneViewOrder,
    ]);

    switch (props.type) {
        case 'WeOwe': {
            serviceTitle = strings.WeOwesHeader;
            serviceDescription = strings.WeOwesDescription;
            if (props.specData?.weOweMsg) {
                serviceDescription = props.specData?.weOweMsg;
            }
            serviceType = 'We Owe';
            if (props.weOwes) {
                serviceList = props.weOwes.map(panel => <AdditionalServicePanel
                    key={panel.weOweDesc}
                    id={panel.weOweDesc}
                    type={serviceType}
                    lit={strings.WeOwe}
                    noAutoRecall={props.noAutoRecall}
                    setNoAutoRecall={props.setNoAutoRecall}
                    vehicles={props.vehicles}
                    selectedVehicle={props.selectedVehicle}
                    theme={props.theme}
                />);
            }
            break;
        }
        case 'Recall': {
            serviceTitle = strings.RecallsHeader;
            serviceType = 'Manufacturer Recall';
            if (props.inquiryInvoked && !props.inquiryReturned && !props.inquiryError) {
                serviceDescription = strings.RetrievingDataFromOEM;
            }
            if (props.inquiryInvoked && props.inquiryReturned && !props.inquiryError && (props.recalls?.length > 0)) {
                props.setWaitOnRecall(false);
                serviceDescription = strings.formatString(strings.RecallsDescription, 'OEM').toString();
                if (props.specData?.recallGreetMsg && props.selectedVehicle) {
                    const VIN = props.vehicles[props.selectedVehicle!].vin;
                    const Make = props.vehicles[props.selectedVehicle!].make;
                    const Model = props.vehicles[props.selectedVehicle!].model;
                    const Year = props.vehicles[props.selectedVehicle!].year;
                    const FirstName = props.apptCust.custFirstName;
                    const LastName = props.apptCust.custLastName;
                    const FullName = FirstName + ' ' + LastName;
                    const VehSeries = props.vehicles[props.selectedVehicle!].series ?
                        props.vehicles[props.selectedVehicle!].series : '';
                    serviceDescription = props.specData?.recallGreetMsg;
                    serviceDescription = serviceDescription.replace('{Make}', Make ?? '');
                    serviceDescription = serviceDescription.replace('{Model}', Model ?? '');
                    serviceDescription = serviceDescription.replace('{Year}', Year ?? '');
                    serviceDescription = serviceDescription.replace('{VIN}', VIN ?? '');
                    serviceDescription = serviceDescription.replace('{First_Name}', FirstName ?? '');
                    serviceDescription = serviceDescription.replace('{Last_Name}', LastName ?? '');
                    serviceDescription = serviceDescription.replace('{Full_Name}', FullName ?? '');
                    serviceDescription = serviceDescription.replace('{Veh_Series}', VehSeries ?? '');
                }
                if (props.recalls) {
                    serviceList = props.recalls.map(panel => <AdditionalServicePanel
                        key={panel.campaignNum}
                        id={panel.campaignDesc}
                        type={serviceType}
                        lit={strings.ManufacturerRecall}
                        noAutoRecall={props.noAutoRecall}
                        setNoAutoRecall={props.setNoAutoRecall}
                        vehicles={props.vehicles}
                        selectedVehicle={props.selectedVehicle}
                        theme={props.theme}
                    />);
                }
            }
            if (props.inquiryReturned && !props.inquiryError && (props.recalls?.length === 0)) {
                props.setWaitOnRecall(false);
                serviceDescription = strings.NoRecalls;
            }
            if (props.inquiryError) {
                props.setWaitOnRecall(false);
                serviceDescription = strings.InquiryError;
            }
            break;
        }
        case 'Recommended': {
            serviceTitle = strings.RecommendedHeader;
            serviceDescription = strings.RecommendedDescription;
            if (props.specData?.allowDoneElsewhere) {
                serviceDescription = strings.RecommendedDescription + '  ' + strings.WorkDoneElsewhere;
            }
            if (props.specData?.prevRecomMsg) {
                serviceDescription = props.specData?.prevRecomMsg;
            }
            serviceType = 'Recommended Last Visit';
            if (props.recom) {
                serviceList = props.recom.map(panel => <AdditionalServicePanel
                    key={panel.opCode}
                    id={panel.opCode}
                    type={serviceType}
                    lit={strings.RecommendedLastVisit}
                    vehLayout={props.vehLayoutCard}
                    noAutoRecall={props.noAutoRecall}
                    setNoAutoRecall={props.setNoAutoRecall}
                    vehicles={props.vehicles}
                    selectedVehicle={props.selectedVehicle}
                    theme={props.theme}
                />);
            }
            break;
        }
        case 'Maintenance Contract': {
            serviceTitle = strings.MaintenanceContracts;
            const serviceDescMid = props.maintContracts.findIndex(mc => mc.status === 'Active') > -1 ?
                strings.MaintContractSubheading2:'';
            serviceDescription = strings.MaintContractSubheading1 + ' <b>' + serviceDescMid + '</b> ' +
                strings.MaintContractSubheading3;
            serviceType = strings.MaintenanceContract;
            if (props.inquiryInvoked && !props.MCReturned && !props.MCError) {
                serviceDescription = strings.RetrievingDataFromOEM;
            }
            if (props.maintContracts.length > 0) {
                props.setWaitOnMC(false);
                serviceList = props.maintContracts.map((panel, index) => <MaintenanceContractCard
                    maintContract={panel} key={'mc'+index}
                    vehicles={props.vehicles}
                    selectedVehicle={props.selectedVehicle}
                    theme={props.theme}
                />);
            }
            if (props.MCReturned && !props.MCError && props.maintContracts.length === 0) {
                serviceDescription = strings.NoMaintContractsFound;
                props.setWaitOnMC(false);
            }
            if (props.MCError) {
                serviceDescription = strings.MaintContractError;
                props.setWaitOnMC(false);
            }
            break;
        }
        default:
            break;
    }

    return (
        <div className={css.section}>
            {props.vehLayoutCard ? '' : (
                <div>
                    <h2 className={
                        props.theme === 'mazda' ? css.headerMazda :
                            props.theme === 'genesis' ? css.headerGenesis :
                                undefined
                    }>
                        {serviceTitle}
                    </h2>
                    <h3>
                        <Fragment>{parse(serviceDescription)}</Fragment>
                    </h3>
                </div>
            )}
            {
                props.type === 'Recall' && !props.vehLayoutCard ?
                    <span
                        className={css.helpMessageRow}
                        style={{ display: 'flex' }}
                    >
                        <div className={helpDisplay}>
                            <IconButton
                                id='recallBtn'
                                onClick={(e) => onClick(e)}
                                variant={'help'}>
                                <div className={recallQuestionClassName}>
                                    <IconHelpCircle />
                                    <div className={recallQuestionClassName}>{strings.RecallsHeader}</div>
                                </div>
                            </IconButton>
                        </div>
                        <div id='helpMini' className={helpDisplayMini}>
                            <IconButton
                                id='recallBtnMini'
                                onClick={(e) => onClick(e)}
                                variant={'help'}>
                                <div className={recallQuestionClassName}>
                                    <IconHelpCircleAlt />
                                    <div className={recallQuestionClassName}>{strings.RecallsHeader}</div>
                                </div>
                            </IconButton>
                        </div>
                    </span> :
                    null
            }
            {
                displayHelpMessage ?
                    <div style={{ display: 'block' }}>
                        <div>
                            <small>
                                {strings.WhatAreRecalls}
                                {toyLexSSC2Launched ? <><br /><span>{strings.WhatIsRemedyNotAvail}</span></> : null}
                                {/* Audi: native anchor with an href so it is focusable; other themes keep the framework Link. */}
                                {props.theme === THEME.AUDI ?
                                    <a
                                        className={recallHelpLinkClass}
                                        id={'WhatAreRecallsLink'}
                                        href={strings.recallLink}
                                        target='_blank'
                                        rel='noreferrer'
                                        onClick={recallLinkHandler}
                                    >
                                        {strings.recallLink}
                                    </a> :
                                    <Link
                                        className={recallHelpLinkClass}
                                        id={'WhatAreRecallsLink'}
                                        url={strings.recallLink}>
                                        {/* url={'https://www.nhtsa.gov/recalls'}> */}
                                        <div onClick={recallLink}>{strings.recallLink}</div>
                                        {/* https://www.nhtsa.gov/recalls */}
                                    </Link>}
                            </small>
                        </div>
                    </div> :
                    null
            }
            {props.vehLayoutCard ? null : <hr className={css.hr} />}
            <div className={css.row}>
                {serviceList}
            </div>
        </div>
    );
};

export default AdditionalService;